import { useState, useEffect } from "react";
import AgentLayout from "@/layouts/AgentLayout";
import { useListAgentProviders } from "@/lib/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, RefreshCcw, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/ui/list-pagination";

export default function AgentProviders() {
  const [limit, setLimit] = useState(10);
  const [user, setUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const { data: providers, isLoading: loadingProviders } = useListAgentProviders();

  const filteredProviders = providers?.filter((p: any) => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      p.specialty?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.npiNumber?.toLowerCase().includes(searchQuery.toLowerCase());
      
    if (!matchesSearch) return false;

    if (dateFilter !== "all" && p.joinedAt) {
      const joinedDate = new Date(p.joinedAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - joinedDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (dateFilter === "7" && diffDays > 7) return false;
      if (dateFilter === "30" && diffDays > 30) return false;
      if (dateFilter === "90" && diffDays > 90) return false;
      if (dateFilter === "custom") {
        if (customStartDate && joinedDate < new Date(customStartDate)) return false;
        if (customEndDate) {
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (joinedDate > endDate) return false;
        }
      }
    }
    
    return true;
  }) || [];

  const totalPages = Math.ceil(filteredProviders.length / pageSize);
  const totalCount = 0; // Default fallback
  const paginatedProviders = filteredProviders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const resetFilters = () => {
    setSearchQuery("");
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  return (
    <AgentLayout>
          <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 sm:lg:xl:sm:lg:sm:lg:xl:space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                  <Stethoscope className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-1">My Providers</h1>
                  <p className="text-muted-foreground">Manage and view all your connected providers.</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search providers..."
                    className="pl-9 bg-white dark:bg-card"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="w-40 shrink-0">
                  <Select value={dateFilter} onValueChange={setDateFilter}>
                    <SelectTrigger className="bg-white dark:bg-card">
                      <SelectValue placeholder="Filter by date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="7">Last 7 Days</SelectItem>
                      <SelectItem value="30">Last 30 Days</SelectItem>
                      <SelectItem value="90">Last 90 Days</SelectItem>
                      <SelectItem value="custom">Custom Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {dateFilter === "custom" && (
                  <div className="flex items-center gap-2">
                    <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="bg-white dark:bg-card w-36" />
                    <span className="text-muted-foreground">-</span>
                    <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="bg-white dark:bg-card w-36" />
                  </div>
                )}
                {(searchQuery || dateFilter !== "all" || customStartDate || customEndDate) && (
                  <Button variant="ghost" size="icon" onClick={resetFilters} title="Reset filters">
                    <RefreshCcw className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white dark:bg-card rounded-2xl border border-border p-6 shadow-sm">
                {loadingProviders ? (
                   <div className="space-y-3"><Skeleton className="w-full h-12" /><Skeleton className="w-full h-12" /><Skeleton className="w-full h-12" /></div>
                ) : filteredProviders.length > 0 ? (
                  <div className="flex flex-col space-y-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                          <tr>
                            <th className="px-4 py-3 rounded-tl-lg">Name</th>
                            <th className="px-4 py-3">Specialty</th>
                            <th className="px-4 py-3">NPI</th>
                            <th className="px-4 py-3 rounded-tr-lg">Joined</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {paginatedProviders.map((p: any) => (
                            <tr key={p.id} className="hover:bg-muted/50 transition-colors">
                              <td className="px-4 py-3 font-medium text-foreground">{p.name}</td>
                              <td className="px-4 py-3 text-muted-foreground">{p.specialty}</td>
                              <td className="px-4 py-3 text-muted-foreground">{p.npiNumber || "N/A"}</td>
                              <td className="px-4 py-3 text-muted-foreground">{p.joinedAt ? new Date(p.joinedAt).toLocaleDateString() : "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="pt-2">
                        <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm py-8 text-center flex flex-col items-center justify-center">
                    <Search className="h-8 w-8 text-muted-foreground/50 mb-3" />
                    <p>No providers found matching your filters.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
    </AgentLayout>
  );
}
